import * as FullStory from "@fullstory/browser";
import { AnalyticsEvent, LoanOfficerDetail, StepResponse } from "pages/application/applicationService";

/**
 * @param stepCode internal code for the step
 * @param stepLabel String name for StepCode
 * @param skipped true if user skip
 */
export function trackFSStepCompleted(stepCode: string, stepLabel: string, skipped: boolean) {
  if (!window.env.ENABLE_FULL_STORY) return;
  FullStory.event("Step completed", {
    StepCode: stepCode,
    StepName: stepLabel,
    Skipped: skipped ? "Yes" : "No",
  });
}

export function trackFSApplicationStarted(props: {
  leadProvider?: string;
  loanType?: string;
  pathSelected?: string;
  leadCampaign?: string;
  applicationStartedDate: string;
  loanOfficerAssigned?: string;
  borrowerType?: string;
}) {
  if (!window.env.ENABLE_FULL_STORY) return;

  const { leadProvider, loanType, pathSelected, leadCampaign, loanOfficerAssigned, applicationStartedDate, borrowerType } = props;

  FullStory.event("Application Started", {
    StartedDate: applicationStartedDate,
    LeadProvider: leadProvider ?? "Unknown",
    LeadCampaign: leadCampaign ?? "Unknown",
    LoanType: loanType ?? "Unknown",
    PathSelected: pathSelected ?? "Unknown",
    LoanOfficerAssigned: loanOfficerAssigned ?? "Not Assigned",
    Environment: window.env.ENVIRONMENT ?? "Unknown",
    borrowerType: borrowerType,
  });
}

const trackFSSummaryEvent = (eventName: string, eventPayload: Record<string, unknown>) => {
  if (!window.env.ENABLE_FULL_STORY) return;

  FullStory.event(eventName, { ...eventPayload });
};

const trackFSSummaryEditEvent = (stepCode: string) => {
  if (!window.env.ENABLE_FULL_STORY) return;

  FullStory.event("Summary Page Edited", {
    SummaryPage: stepCode,
  });
};

export const trackFSInitAppSubmittedEvent = () => {
  if (!window.env.ENABLE_FULL_STORY) return;

  FullStory.event("Initial App Submitted", {
    DateSubmitted: new Date().toISOString(),
  });
};

const trackFSOptedVoaEvent = (orderDate: Date) => {
  if (!window.env.ENABLE_FULL_STORY) return;

  FullStory.event("Opted Voa", { VoaOrderDate: orderDate });
};

const trackFSOptedVoiEvent = (orderDate: Date) => {
  if (!window.env.ENABLE_FULL_STORY) return;

  FullStory.event("Opted VOI", { VoiOrderDate: orderDate });
};

const trackFSLOAssignedEvent = (loanOfficerDetails: LoanOfficerDetail, stepData: StepResponse) => {
  if (!window.env.ENABLE_FULL_STORY) return;
  FullStory.event("Loan Officer Assigned", {
    LoanOfficerName: loanOfficerDetails.fullName,
    LoanOfficerNMLS: loanOfficerDetails.nmlsLicenseNumber,
    StepCode: stepData.stepCodeText,
    Section: stepData.section,
  });
};

export function trackFSVisitedRegistrationPage(props:{
  leadProvider?: string;
  loanType?: string;
}) {
  if (!window.env.ENABLE_FULL_STORY) return;

  const { leadProvider, loanType } = props;

  FullStory.event("Visited Registration Page", {
    LeadProvider: leadProvider ?? "Unknown",
    LoanType: loanType ?? "Unknown",
  });
}

const initFullStory = () => {
  if (!window.env.ENABLE_FULL_STORY) return;
  FullStory.init({ orgId: window.env.FULLSTORY_ORG_ID });
};

const setFSNavigatedToInsuranceCustomEvent = (nhDealApplicationId: string, redirectionUrl: string) => {
  if (!window.env.ENABLE_FULL_STORY) return;

  FullStory.event("NavigatedToInsurance", {
    nhDealApplicationId,
    redirectionUrl,
  });
};

const setFSUserProperties = (email?: string, lastName?: string) => {
  if (window.env.ENABLE_FULL_STORY && email) {
    FullStory.setUserVars({ email });
  }
  if (window.env.ENABLE_FULL_STORY && lastName) {
    FullStory.setUserVars({ lastName });
  }
};

interface AnalyticsSection {
  section: string;
  eventType: AnalyticsEvent;
  displayName: string;
}

export const analyticsSections: AnalyticsSection[] = [
  { section: "Lead", eventType: "FSLeadCompleted", displayName: "Lead Completed" },
  { section: "Profile", eventType: "FSProfileCompleted", displayName: "Profile Completed" },
  { section: "Property", eventType: "FSPropertyCompleted", displayName: "Property Completed" },
  { section: "Credit", eventType: "FSCreditCompleted", displayName: "Credit Completed" },
  { section: "Finances", eventType: "FSFinanceCompleted", displayName: "Finance Completed" },
  { section: "Acknowledgements", eventType: "FSDeclarationsCompleted", displayName: "Declarations Completed" },
];

export {
  initFullStory,
  setFSUserProperties,
  setFSNavigatedToInsuranceCustomEvent,
  trackFSSummaryEvent,
  trackFSSummaryEditEvent,
  trackFSOptedVoaEvent,
  trackFSLOAssignedEvent,
  trackFSOptedVoiEvent,
};
